// Кооперативные инстансы на карте (E04-T07, ux/04 §1–3, ux/07 §6): точка с вызванным инстансом, MOD-BOSS в режиме
// «Кооператив», MOD-COOP-BOSS-CALL (председатель и заместитель, 1 объявление о сборе) и MOD-INSTANCE-RESULT.
// Числа (HP, срок, вклад, допуск) — только с сервера: GET /districts, GET /bosses/{id}/instance, вклады.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api, ResponseOf } from '@garages/contract';
import { useApp } from '../app/app.js';
import { bossInfo, itemName } from '../content/catalog.js';
import { amount, duration, num } from '../i18n/format.js';
import { t as tr } from '../i18n/t.js';
import { ApiCallError } from '../net/api-client.js';
import { onWs } from '../net/ws.js';
import { clientConfig, districts, online, saveCache, wallet, type Districts } from '../state/game.js';
import { coopBrief } from '../state/coop.js';
import { storage } from '../state/storage.js';
import type { SheetProps } from '../ui/App.js';
import { ErrorBanner, Sheet, useNow } from '../ui/kit.js';
import { showToast } from '../ui/toast.js';
import { useLoad } from '../ui/use-load.js';
import { LAST_BATTLE_KEY } from '../boot/boot.js';
import { I, ICONS } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { sourceName } from './boxes.js';
import '../ui/store.css';

type Instance = ResponseOf<Api['getBossInstance']>;
type Boss = Districts['districts'][number]['bosses'][number];
export type CoopInst = NonNullable<Boss['coopInstance']>;

/** Сколько осталось до сгорания и «горит» ли (меньше 2 ч — красным, ux/04 §1.2). */
export function burnsIn(endsAt: string, now: number): { ms: number; hot: boolean; over: boolean } {
  const ms = Date.parse(endsAt) - now;
  return { ms: Math.max(0, ms), hot: ms > 0 && ms < 2 * 3_600_000, over: ms <= 0 };
}

/** Все живые кооперативные инстансы на карте (по точкам боссов). */
export function coopInstances(d: Districts | null): { bossId: string; inst: CoopInst }[] {
  return (d?.districts ?? []).flatMap((x) => x.bosses.flatMap((b) => (b.coopInstance ? [{ bossId: b.bossId, inst: b.coopInstance }] : [])));
}

/** Вызывать может только председатель и заместитель (ГД 07 §6, ux/07 §6). */
export const canCallBoss = (role: string | null | undefined) => role === 'chairman' || role === 'deputy';

/** Полоска общего HP и «сгорит через N» на точке и плашке. */
export function CoopHp(p: { inst: CoopInst; compact?: boolean }) {
  const now = useNow(30_000);
  const b = burnsIn(p.inst.endsAt, now);
  return (
    <span class={`coop-hp ${p.compact ? 'compact' : ''}`}>
      <span class="progress coop"><i style={{ width: `${Math.round((p.inst.hp / p.inst.hpMax) * 100)}%` }} /></span>
      <span class={`small ${b.hot ? 'neg' : 'muted'}`} data-testid="burns-in">{b.over ? tr('CL-INST-BURNED') : tr('CL-INST-BURNS', { t: duration(b.ms) })}</span>
    </span>
  );
}

/** Плашка кооперативного инстанса над лентой районов (ux/04 §1.1) и кнопка вызова для руководства. */
export function CoopStrip(p: { data: Districts | null }) {
  const app = useApp();
  const list = coopInstances(p.data);
  const role = coopBrief.value?.role;
  if (!coopBrief.value) return null;
  return (
    <div class="col coop-strip" style={{ gap: 6 }}>
      {list.map(({ bossId, inst }) => (
        <button key={inst.iid} class="card coop-plaque" data-testid={`coop-plaque-${bossId}`} onClick={() => app.nav.openSheet('MOD-BOSS', { bossId, mode: 'coop' })}>
          <span class="coop-plaque-icon"><Icon id={`ICO-BOSS-${bossId}`} fallback={ICONS.boss[1]} size={40} /></span>
          <span class="col" style={{ gap: 2, flex: 1, minWidth: 0, textAlign: 'left' }}>
            <b>{tr('CL-INST-COOP', { boss: bossInfo(bossId).short })}</b>
            <CoopHp inst={inst} />
          </span>
          <span class="accent-text">›</span>
        </button>
      ))}
      {canCallBoss(role) && !list.length ? (
        <button class="btn small outline" data-testid="open-boss-call" onClick={() => app.nav.openSheet('MOD-COOP-BOSS-CALL')}><I n="map" size={18} />{tr('CL-CALL-OPEN')}</button>
      ) : null}
    </div>
  );
}

const CALL_ERR: Record<string, string> = {
  INSTANCE_ACTIVE: 'CL-CALL-ERR-ACTIVE', NO_SUMMONS: 'CL-CALL-NO-SUMMONS', BOSS_NOT_ELIGIBLE: 'CL-CALL-ERR-ELIGIBLE',
  BOSS_LOCKED: 'CL-CALL-ERR-LOCKED', FORBIDDEN_ROLE: 'CL-CALL-ERR-ROLE',
};

type Calls = ResponseOf<Api['getCoopBossCalls']>;
type Candidate = Calls['candidates'][number];

/** Подпись кандидата (ux/07 §6): не открыт тебе, «Победили 3 из 5», HP инстанса и срок. */
export function candidateNote(c: Candidate): string {
  if (c.lockedForMe) return tr('CL-CALL-LOCKED');
  if (c.winners < c.need) return tr('CL-CALL-WINNERS', { k: c.winners, n: c.need });
  return c.hpMax ? tr('CL-CALL-HP', { hp: num(c.hpMax) }) : tr('CL-CALL-TERM');
}

/** MOD-COOP-BOSS-CALL: кандидаты B01–B12 из GET /coops/{id}/boss-calls (Р-89), вызов тратит одно объявление о сборе. */
export function BossCallSheet({ entry }: SheetProps) {
  const app = useApp();
  const coop = coopBrief.value;
  const load = useLoad(() => (coop ? app.call('getCoopBossCalls', { params: { coopId: coop.coopId } }) : Promise.reject(new Error('no coop'))));
  const data = load.data;
  const [pick, setPick] = useState<string | null>(null);
  const key = useRef(app.idem());
  const call = async () => {
    if (!coop || !pick) return;
    try {
      const r = await app.call('callCoopBoss', { params: { coopId: coop.coopId }, body: { bossId: pick }, idempotencyKey: key.current });
      key.current = app.idem();
      app.haptic('success');
      // Точка на карте сразу получает рамку и HP; полная карта перечитается при возврате.
      if (districts.value) {
        const next = structuredClone(districts.value.data);
        for (const x of next.districts) for (const b of x.bosses) if (b.bossId === pick) b.coopInstance = { iid: r.instance.iid, hp: r.instance.hp, hpMax: r.instance.hpMax, endsAt: r.instance.expiresAt };
        districts.value = { data: next, at: Date.now() };
        saveCache();
      }
      showToast(tr('CL-CALL-DONE', { boss: bossInfo(pick).short }), 'success');
      app.nav.openSheet('MOD-BOSS', { bossId: pick, mode: 'coop' }, { replace: true });
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code !== 'NETWORK' && code !== 'TIMEOUT') key.current = app.idem();
      app.haptic('error');
      if (CALL_ERR[code]) { showToast(tr(CALL_ERR[code]!), 'error'); load.reload(); return; }
      app.handleError(e);
    }
  };
  const chosen = data?.candidates.find((c) => c.bossId === pick);
  let main: { text: string; disabled: boolean };
  if (!online.value) main = { text: tr('UI-OFFLINE'), disabled: true };
  else if (!canCallBoss(coop?.role)) main = { text: tr('CL-CALL-ERR-ROLE'), disabled: true };
  else if (data?.active) main = { text: tr('CL-CALL-ALREADY', { boss: bossInfo(data.active.bossId).short }), disabled: true };
  else if (data && data.summons < 1) main = { text: tr('CL-CALL-NO-SUMMONS'), disabled: true };
  else if (!chosen) main = { text: tr('CL-CALL-PICK'), disabled: true };
  else main = { text: tr('CL-CALL-GO', { boss: bossInfo(chosen.bossId).short }), disabled: !chosen.eligible };
  return (
    <Sheet entry={entry} title={tr('CL-CALL-TITLE')} actions={[{ text: main.text, testId: 'call-go', disabled: main.disabled, onClick: call }]}>
      <div class="col" data-testid="MOD-COOP-BOSS-CALL-body">
        {data ? <span class="badge soft" data-testid="call-summons">{tr('CL-CALL-SUMMONS', { k: data.summons, n: data.summonsMax })}</span> : null}
        {load.error && !data ? <ErrorBanner what={tr('CL-CALL-TITLE')} onRetry={load.reload} /> : null}
        {!data && load.loading ? <span class="skeleton" style={{ height: 200 }} /> : null}
        {data && data.summons < 1 ? <span class="small muted" data-testid="call-no-summons">{tr('CL-CALL-NO-SUMMONS-WHY')}</span> : null}
        {data?.active ? <span class="small" data-testid="call-active">{tr('CL-CALL-ALREADY', { boss: bossInfo(data.active.bossId).name })}</span> : null}
        <span class="small muted">{tr('CL-CALL-RULES')}</span>
        <div class="call-list" role="radiogroup">
          {data?.candidates.map((c) => (
            <button key={c.bossId} role="radio" aria-checked={pick === c.bossId} class={`list-row call-row ${pick === c.bossId ? 'on' : ''}`} disabled={!c.eligible}
              data-testid={`call-${c.bossId}`} onClick={() => { app.haptic('selection'); setPick(c.bossId); }}>
              <Icon id={`ICO-BOSS-${c.bossId}`} fallback={ICONS.boss[1]} size={36} />
              <span class="col" style={{ gap: 2, flex: 1, textAlign: 'left' }}>
                <b>{bossInfo(c.bossId, clientConfig.value).name}</b>
                <span class={`small ${c.eligible ? 'muted' : ''}`}>{candidateNote(c)}</span>
              </span>
            </button>
          ))}
        </div>
        <span class="small muted">{tr('CL-CALL-NEWBIE')}</span>
      </div>
    </Sheet>
  );
}

/** Тело MOD-BOSS в режиме «Кооператив»: общее HP, срок, мой вклад и допуск, «В бой» в общий инстанс. */
export function CoopBossBody(p: { bossId: string; onResult: (inst: Instance) => void }) {
  const app = useApp();
  const load = useLoad(() => app.call('getBossInstance', { params: { bossId: p.bossId }, query: { mode: 'coop' } }));
  const [live, setLive] = useState<{ hp: number; hpMax: number } | null>(null);
  useEffect(() => onWs((ev) => { if (ev.t === 'boss.hp' && ev.d.iid === load.data?.iid) setLive({ hp: ev.d.hp, hpMax: ev.d.hpMax }); }), [load.data?.iid]);
  const inst = load.data;
  if (load.error && !inst) return <ErrorBanner what={tr('CL-INST-WHAT')} onRetry={load.reload} />;
  if (!inst) return <span class="skeleton" style={{ height: 120 }} />;
  const hp = live ?? inst;
  const done = inst.status !== 'active';
  return (
    <div class="col" data-testid="coop-boss" data-status={inst.status}>
      <span class="badge" style={{ alignSelf: 'flex-start' }}>{tr('CL-INST-MODE')}</span>
      <CoopHp inst={{ iid: inst.iid, hp: hp.hp, hpMax: hp.hpMax, endsAt: inst.expiresAt }} />
      <span class="small">{tr('CL-INST-HP', { hp: num(hp.hp), max: num(hp.hpMax) })} · {tr('CL-INST-PARTICIPANTS', { n: inst.participants })}</span>
      <div class="card col" style={{ gap: 2 }}>
        <span>{tr('CL-INST-MY', { n: num(inst.me.damage) })}</span>
        <span class={`small ${inst.me.qualified ? 'pos' : 'muted'}`}>{inst.me.qualified ? tr('CL-INST-QUALIFIED') : tr('CL-INST-NOT-QUALIFIED')}</span>
        {!inst.me.admitted ? <span class="small neg">{tr('CL-INST-NOT-ADMITTED')}</span> : null}
      </div>
      {inst.top.length ? (
        <ol class="inst-top small" data-testid="coop-top">
          {inst.top.slice(0, 5).map((t) => <li key={t.publicId}><span>{t.displayName}</span><b>{num(t.damage)}</b></li>)}
        </ol>
      ) : null}
      {done ? <button class="btn small accent" data-testid="inst-result-open" onClick={() => p.onResult(inst)}>{tr('CL-INST-RESULT-OPEN')}</button> : null}
    </div>
  );
}

/** «В бой» в общий инстанс: POST /battles {mode: 'coop'} с одним ключом на повторы. */
export function useCoopFight(bossId: string) {
  const app = useApp();
  const key = useRef(app.idem());
  return async () => {
    const w = wallet.value;
    try {
      const start = await app.call('startBattle', { body: { bossId, mode: 'coop' }, idempotencyKey: key.current });
      storage.setJson(LAST_BATTLE_KEY, { battleId: start.battleId, seen: false, origin: bossId });
      if (w) wallet.value = { ...w, energy: { ...w.energy, value: start.energy.value, nextAt: start.energy.nextAt } };
      app.nav.closeSheets();
      app.nav.push('SCR-FIGHT', { start });
    } catch (e) {
      const code = e instanceof ApiCallError ? e.code : 'INTERNAL';
      if (code !== 'NETWORK' && code !== 'TIMEOUT') key.current = app.idem();
      if (code === 'BOSS_DEFEATED' || code === 'GONE') { app.nav.openSheet('MOD-INSTANCE-RESULT', { bossId }, { replace: true }); return; }
      if (code === 'NOT_ENOUGH_ENERGY') { app.nav.openSheet('MOD-ENERGY', { need: 10 }); return; }
      if (code === 'NOT_IN_COOP') { showToast(tr('CL-INST-NOT-IN-COOP'), 'error'); return; }
      app.handleError(e, { resource: tr('CL-SCREENS-MAP-22') });
    }
  };
}

type Result = ResponseOf<Api['getInstanceResult']>;

/** «Сгорел и снято < 50%» — награды нет (Р-60, ux/04 §3). */
export function noRewardOf(r: Pick<Result, 'outcome' | 'hpRemovedPct'>): boolean {
  return r.outcome === 'expired' && r.hpRemovedPct < 50;
}

const pct1 = (n: number) => (Math.round(n * 10) / 10).toString().replace('.', ',');

/**
 * MOD-INSTANCE-RESULT (ux/04 §3): «Побеждён» или «Ушёл», последний удар, мой вклад и допуск, доля (Base + котёл),
 * шанс и итог розыгрыша детали — из GET /bosses/instances/{iid}/result (Р-89). Награда уже начислена при
 * финализации; «Забрать» только подтверждает показ (ack). До финализации сервер отвечает 409 — «Считаем итог».
 */
export function InstanceResultSheet({ entry }: SheetProps) {
  const app = useApp();
  const bossId = String(entry.params['bossId']);
  const [iid, setIid] = useState<string | null>((entry.params['iid'] as string | undefined) ?? (entry.params['instance'] as Instance | undefined)?.iid ?? null);
  // Открыли по боссу без iid (BOSS_DEFEATED при входе) — берём iid из инстанса.
  useEffect(() => { if (!iid) app.call('getBossInstance', { params: { bossId }, query: { mode: 'coop' } }).then((i) => setIid(i.iid)).catch(() => {}); }, []);
  const [n, setN] = useState(0);
  const [res, setRes] = useState<Result | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!iid) return;
    let alive = true;
    setErr(null);
    app.call('getInstanceResult', { params: { iid } }).then((r) => { if (alive) setRes(r); })
      .catch((e: unknown) => { if (alive) setErr(e instanceof ApiCallError ? e.code : 'INTERNAL'); });
    return () => { alive = false; };
  }, [iid, n]);
  // Итог ещё считается (409): переспрашиваем раз в 5 с.
  useEffect(() => { if (err !== 'CONFLICT') return; const t = setTimeout(() => setN((x) => x + 1), 5000); return () => clearTimeout(t); }, [err, n]);
  const info = bossInfo(bossId, clientConfig.value);
  const ack = () => {
    if (!res || res.seen) return;
    void app.call('ackInstanceResult', { params: { iid: res.iid }, body: {} }).catch(() => {});
    // Подарок на точке гаснет сразу, не дожидаясь перечитки карты.
    if (districts.value) {
      const next = structuredClone(districts.value.data);
      for (const x of next.districts) for (const b of x.bosses) if (b.coopResult?.iid === res.iid) delete b.coopResult;
      districts.value = { data: next, at: districts.value.at };
    }
  };
  if (!res) {
    return (
      <Sheet entry={entry} title={info.name}>
        <div class="col" data-testid="MOD-INSTANCE-RESULT-body" data-outcome="pending">
          {err === 'CONFLICT' ? <div class="banner" data-testid="inst-counting"><span class="spinner" aria-hidden />{tr('CL-INST-COUNTING')}</div>
            : err ? <ErrorBanner what={tr('CL-INST-WHAT')} onRetry={() => setN((x) => x + 1)} />
              : <span class="skeleton" style={{ height: 160 }} />}
        </div>
      </Sheet>
    );
  }
  const none = noRewardOf(res);
  const actions = none
    ? [{ text: tr('CL-INST-NEW-FIGHT'), icon: 'energy' as const, testId: 'inst-new', disabled: !online.value, onClick: () => { ack(); app.nav.closeSheets(); app.nav.openSheet('MOD-BOSS', { bossId }); } }]
    : [
      { text: tr('CL-INST-TABLE'), tone: 'outline' as const, testId: 'inst-table', onClick: () => app.nav.openSheet('MOD-CONTRIBUTION', { contribution: { rank: 0, damage: res.me.damage, qualified: res.me.qualified } }) },
      { text: tr('CL-INST-TAKE'), testId: 'inst-take', onClick: () => {
        ack();
        if (res.reward) app.nav.openSheet('MOD-REWARD', { grant: res.reward }, { replace: true }); else app.nav.popSheet();
      } },
    ];
  const won = res.outcome === 'defeated';
  return (
    <Sheet entry={entry} title={info.name} actions={actions}>
      <div class="col" style={{ alignItems: 'center', textAlign: 'center' }} data-testid="MOD-INSTANCE-RESULT-body" data-outcome={won ? 'win' : 'gone'}>
        <span class={`inst-ribbon ${won ? 'win' : 'gone'}`}>{won ? tr('CL-INST-WON') : tr('CL-INST-GONE')}</span>
        <Icon id={`FIG-${bossId}`} fallback={ICONS.boss[1]} size={96} />
        <span class="small muted">{tr('CL-INST-REMOVED', { p: pct1(res.hpRemovedPct) })}{res.lastHit ? ` · ${tr('CL-INST-LAST-HIT', { name: res.lastHit.displayName })}` : ''}</span>
        <div class="card col" style={{ gap: 4, width: '100%', textAlign: 'left' }}>
          <span>{tr('CL-INST-MY', { n: num(res.me.damage) })}</span>
          {!none ? <span class={`small ${res.me.qualified ? 'pos' : 'muted'}`}>{res.me.qualified ? tr('CL-INST-QUALIFIED') : tr('CL-INST-NOT-QUALIFIED')}</span> : null}
          {!none && !res.me.admitted ? <span class="small neg">{tr('CL-INST-NOT-ADMITTED')}</span> : null}
        </div>
        {none ? <div class="banner warn" data-testid="inst-no-reward">{tr('CL-INST-NO-REWARD', { p: pct1(res.hpRemovedPct) })}</div> : null}
        {!none && res.reward ? (
          <div class="col" style={{ gap: 4, width: '100%' }} data-testid="inst-reward">
            <b class="small">{tr('CL-INST-SHARE')}</b>
            <div class="row" style={{ flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
              {Object.entries(res.reward.currencies ?? {}).map(([k, v]) => <span key={k} class="badge soft">{amount(k, v)}</span>)}
              {(res.reward.items ?? []).map((i) => <span key={i.defId} class="badge soft">{i.defId.startsWith('EQ-') ? itemName(i.defId) : sourceName(i.defId)}{i.qty > 1 ? ` ×${i.qty}` : ''}</span>)}
            </div>
          </div>
        ) : null}
        {!none && !res.reward ? <div class="banner" data-testid="inst-no-share">{tr('CL-INST-NO-SHARE')}</div> : null}
        {!none && res.pot.chancePct > 0 ? (
          <span class="small" data-testid="inst-pot">{tr('CL-INST-POT', { p: pct1(res.pot.chancePct) })} · {res.pot.item ? tr('CL-INST-POT-WON', { item: itemName(res.pot.item.defId) }) : tr('CL-INST-POT-MISS')}</span>
        ) : null}
        {res.mailed ? <span class="small muted" data-testid="inst-mailed">{tr('CL-INST-MAILED')}</span> : null}
      </div>
    </Sheet>
  );
}
